import { MessageSquare } from 'lucide-react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { NOTIFICATIONS_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import {
  collectErrors,
  validateRequiredString,
  validateRequiredTemplate,
  validateTemplateChunks,
} from '../../validation';
import { SmsActionCustomization } from './SmsActionCustomization';
import { defaultNodeData } from '../../graphUtils';

/** Validation field names for the SMS action. Shared with the UI. */
export const SmsActionFields = {
  RECIPIENT: 'Recipient',
  MESSAGE_TEMPLATE: 'Message template',
} as const;

export const SmsActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'sms_action',

  category: NodeCategory.NOTIFICATIONS,

  label: 'SMS',

  hasInput: true,

  getIcon: (size: number) => (
    <MessageSquare size={size} color={NOTIFICATIONS_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const smsData = data.sms_action;
    if (!smsData) return undefined;

    if (smsData.wait_for_response) {
      return 'Wait for response';
    }
    return smsData.contact_id ? 'Send only' : '(unconfigured)';
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const smsData = nodeData.sms_action;
    return collectErrors(
      validateRequiredString(smsData?.contact_id, SmsActionFields.RECIPIENT),
      validateRequiredTemplate(
        smsData?.message_chunks ?? [],
        SmsActionFields.MESSAGE_TEMPLATE
      ),
      ...validateTemplateChunks(
        smsData?.message_chunks ?? [],
        nodeId,
        graph,
        SmsActionFields.MESSAGE_TEMPLATE
      )
    );
  },

  CustomizationComponent: SmsActionCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('sms_action', {
      phone: '',
      message_chunks: [],
      contact_id: '',
      wait_for_response: false,
      update_coc: false,
    }),

  showCustomizePanelOnCreation: true,
};
